import React, { useEffect, useMemo, useRef, useState } from 'react';
import { departmentOpenCounts, ALL_DEPARTMENTS } from '@battleops/shared';
import { useDepartments } from '@battleops/client/src/context/DepartmentContext';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useProjects } from '@battleops/client/src/context/ProjectContext';
import Icon from '../components/Icon';
import s from './DepartmentSwitcher.module.css';

// The department switcher under you in the rail — the phone drawer's (mobile DepartmentSwitcher), desk-sized.
// ‹ / › step, click the name for the list, hold the name to send it to your other devices. Each device keeps its own.
export default function DepartmentSwitcher() {
  const { departments, departmentActive, departmentOptions, departmentLocked, currentDepartment,
    setCurrentDepartment, stepDepartment, syncDepartment } = useDepartments();
  const { tasks } = useTasks();
  const { projects } = useProjects();
  const [open, setOpen] = useState(false);
  const [synced, setSynced] = useState(false);
  const holdRef = useRef(null);
  const heldRef = useRef(false);
  const boxRef = useRef(null);
  useEffect(() => { if (!synced) return undefined; const t = setTimeout(() => setSynced(false), 2200); return () => clearTimeout(t); }, [synced]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const counts = useMemo(() => (open ? departmentOpenCounts(tasks, departments, projects) : null), [open, tasks, departments, projects]);
  if (!departmentActive || !currentDepartment) return null;

  const tint = currentDepartment.color || 'var(--c-accent)';
  const label = currentDepartment.id === ALL_DEPARTMENTS ? 'ALL' : String(currentDepartment.name).toUpperCase();
  const countOf = (id) => (id === ALL_DEPARTMENTS ? [...(counts?.values() || [])].reduce((a, b) => a + b, 0) : counts?.get(id) ?? 0);
  const down = () => {
    heldRef.current = false;
    holdRef.current = setTimeout(() => { heldRef.current = true; syncDepartment().then(() => setSynced(true)).catch(() => {}); }, 450);
  };
  const up = () => clearTimeout(holdRef.current);

  if (departmentLocked) {
    return <div className={s.wrap}><div className={`${s.row} ${s.locked}`} style={{ '--dc': tint }}><span className={s.dot} /><span className={s.name}>{label}</span></div></div>;
  }
  return (
    <div className={s.wrap} ref={boxRef}>
      <div className={s.row} style={{ '--dc': tint }}>
        <button className={s.arrow} onClick={() => stepDepartment(-1)} aria-label="Previous department" title="Previous department"><Icon name="chevron-left" size={16} /></button>
        <button className={s.center} onMouseDown={down} onMouseUp={up} onMouseLeave={up}
          onClick={() => { if (!heldRef.current) setOpen((o) => !o); }} aria-haspopup="listbox" aria-expanded={open}
          title="Click to choose · hold to sync to your other devices">
          <span className={s.dot} /><span className={s.name}>{label}</span>
        </button>
        <button className={s.arrow} onClick={() => stepDepartment(1)} aria-label="Next department" title="Next department"><Icon name="chevron-right" size={16} /></button>
      </div>
      <div className={s.hint}>{synced ? 'Synced to your other devices' : 'Click to choose · hold to sync'}</div>
      {open ? (
        <div className={s.menu} role="listbox" aria-label="Department">
          {departmentOptions.map((d) => {
            const on = d.id === currentDepartment.id;
            return (
              <button key={d.id} role="option" aria-selected={on} className={s.option} data-on={on || undefined} style={{ '--dc': d.color || 'var(--c-accent)' }}
                onClick={() => { setCurrentDepartment(d.id); setOpen(false); }}>
                <span className={s.swatch}>{d.icon}</span><span className={s.optName}>{d.name}</span>
                <span className={s.count}>{countOf(d.id)} open</span>{on ? <Icon name="check" size={14} /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
